import {areSetsEqual, noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useRef, useEffect} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ContextMenuOption} from 'components/ContextMenu';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import AIDataEntryButton from 'notebook/ai-tools/AIDataEntryButton';
import {
  AddStructuredTableRowsUsingAIResult,
  SUPPORTED_TABLE_KINDS_FOR_AI_DATA_ENTRY,
} from 'notebook/ai-tools/flexible-data-entry/addStructuredTableRowsUsingAI';
import resolveStaleComputingCells from 'notebook/integrations/registry/resolveStaleComputingCells';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {
  forwardSearchResultProps,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {NoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import * as NoteEditorTableUtil from 'notebook/notes/plugins/plain-table/NoteEditorTableUtil';
import addSamplesToTable, {AddToTableOptions} from 'notebook/sample-propagation/util/addSamplesToTable';
import getColumnSortingContextMenuOptions from 'notebook/structured-tables/column-sorting/getColumnSortingMenuOptions';
import useGetSingleInputContextMenuOptions from 'notebook/structured-tables/single-inputs/util/useGetSingleInputContextMenuOptions';
import useUnmergeBrokenSingleInputsCallback, {
  getBrokenSingleInputIndexes,
} from 'notebook/structured-tables/single-inputs/util/useUnmergeBrokenSingleInputsCallback';
import {DESTINATION_TABLE_TYPES} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {
  BasicTableProps,
  LastSyncedUpstreamValue,
  StatusFilter,
  StructuredTable,
  StructuredTableAdapter,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import getUnifiedAddSampleMenuOptions from 'notebook/structured-tables/unified-add-from-structured-table/getUnifiedAddFromStructuredTableMenuOptions';
import canAttachFileToCellInTable from 'notebook/structured-tables/util/canAttachFileToCellInTable';
import getAddFromSourceButton from 'notebook/structured-tables/util/getAddFromSourceButton';
import getFooterInformation from 'notebook/structured-tables/util/getFooterInformation';
import {StructuredTableInsertionContext} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import updateTableWithColumnUpdatesAndCells from 'notebook/structured-tables/util/updateTableWithColumnUpdatesAndCells';
import useStructuredTableContexts from 'notebook/structured-tables/util/useStructuredTableContexts';
import TableEditable from 'notebook/tables/core-ui/TableEditable';
import {Selector} from 'notebook/tables/excess/Evaluator';
import {hasAnyErrors, hasAnyWarnings} from 'notebook/tables/excess/Util';
import {TableOpContext, useTableOps} from 'notebook/tables/spreadsheets/TableOpContext';
import {StructuredTableToTableMetadata, TableData, TableOpOptions} from 'notebook/tables/TableConstants';
import * as TableConstants from 'notebook/tables/TableConstants';
import {
  getLockedCellWarningMessage,
  getLockedTableWarningMessage,
  LockedCellWarningType,
  LockedTableWarningType,
} from 'notebook/tables/util/LockedTableWarningMessages';
import {FileAnalyticsParams} from 'notebook/util/analytics/FileAnalyticsUtils';
import convertFileToCellData from 'notebook/util/ConvertFileToCellData';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import APIUtil from 'util/APIUtil';
import {confirm} from 'util/Modals';
import {BasicEvent, Optional} from 'util/Types';

import {useActionFunctionContext} from './action-functions/ActionFunctionContext';
import getCustomColumnMenuOptions from './custom-columns/getCustomColumnMenuOptions';
import useHydratedStructuredTableData from './hooks/useHydratedStructuredTableData';
import getLookupColumnMenuOptions from './lookup-columns/getLookupColumnMenuOptions';
import getStructuredTableMappingContextMenuOptions from './structured-table-mappings/getStructuredTableMappingContextMenuOptions';
import {
  StructuredTableMappingsStateManager,
  useStructuredTableMappingsState,
} from './structured-table-mappings/StructuredTableMappingsState';
import getActionFunctions, {GetActionFunctionsParams} from './util/getActionFunctions';
import getInsertableColumnInfos from './util/getInsertableColumnInfos';
import {
  performRowDeletionWithDataMods,
  performNonContiguousRowDeletionWithDataMods,
  performColumnDeletionWithDataMods,
  performColumnCreationWithDataMods,
} from './util/performTableOpWithDataMods';
import {withStructuredTableColumnNameSet} from './util/StructuredTableColumnUtil';
import StructuredTableContext from './util/StructuredTableContext';
import useAllowMergeSelectionCallback from './util/useAllowMergeSelectionCallback';

/**
 * These are the props needed to display the StructuredTableView.
 */
export interface StructuredTableViewProps<TTableMetadata, TRowMetadata> extends MediocreSearchResultProps {
  // The NoteEditor showing this table
  editor: NoteEditor;

  // The props from the Editor
  editorProps: NoteEditorProps<NotePart | NoteLinkedGroup<unknown>>;

  // Client id of the note part containing the table
  notePartCid: string;

  // The index in the NoteParts array that this table is located at
  index: number;

  // A grab bag of props that are applicable for Table items - `forceLiveness` is not actually used, but
  // passing it down to this component ensures that it will re-render / call `useHydratedStructuredTableData`
  // when appropriate.
  itemProps: TableItemProps & {forceLiveness?: number};

  // Basic props such as table name an icon that are derived from the table kind
  basicTableProps: BasicTableProps;
  overrideDefaultTableProps?: Partial<StructuredTableDefaultTableEditableProps>;

  // Our event handlers
  mouseDownHandler?: (index: number, e: BasicEvent) => void;
  mouseOverHandler?: (index: number, e: BasicEvent) => void;

  // The StructuredTable we are displaying in this component
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;

  // The StructuredTableAdapter which we will use to turn our StructuredTable into a Table and back.
  structuredTableAdapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>;

  // Our updateHandler which gets threaded back to the StructuredTablePlugin.setContent.  It needs to know
  // how to handle updating the StructuredTable from a TableData or a StructuredTable format.  It also needs
  // to know how to update the LastSyncedUpstreamValues, if provided.
  updateHandler: (
    index: number,
    content: {
      item?: StructuredTable<TTableMetadata, TRowMetadata>;
      table?: TableData;
      lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>;
    },
    selectors?: never,
    tableOp?: TableConstants.TableOpDiffMap
  ) => void;

  selectorsById: {[selectorId: string]: SchemaFieldSelector} | null;
}

export interface StructuredTableDefaultTableEditableProps {
  isExpanded: boolean;
  hasPlaceholderRows: boolean;
  selectorsEnabled: boolean;
  openManageSelectorsModal: () => void;
  showFormulaBox: boolean;
  canAttachFileToCell: ([i, j]: Array<number>) => boolean;
  convertFileToCellData?: (file: File) => Promise<{text: string; cellData: Partial<TableConstants.Cell>}>;
  index: number;
  getEditWhileCellLockedWarning: () => string;
  disableMaximizedTables: boolean;
  canAddRemoveColumns: boolean;
  canAddColumnsFreeform: boolean;
  disableFormulas: boolean;
  selectorsLink: {
    value: Array<Selector>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    requestChange?: () => any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectorUsageCounts: any;
  };
  disableLegacyHeader: boolean;
  isLeftAlignColumnHeader: boolean;
  forceCollapsed: boolean;
  isCompactBottomSpacing: boolean;
  isCollapsible: boolean;
  editWhileTableLockedWarning: string;
  isNestedInParentComponent: boolean;
  withColumnNameSet: (table: TableConstants.Table, i: number, label: string) => TableConstants.Table;
}

const genericLockedCellWarningMessage = (): string =>
  getLockedCellWarningMessage(LockedCellWarningType.GENERIC);

function defaultProps<TTableMetadata, TRowMetadata>(
  props: StructuredTableViewProps<TTableMetadata, TRowMetadata>
): StructuredTableDefaultTableEditableProps {
  const canAttachFileToCell = ([i, j]: Array<number>): boolean =>
    canAttachFileToCellInTable([i, j], props.structuredTable);
  return {
    index: props.index,
    selectorsLink: {
      value: [],
      selectorUsageCounts: {},
    },
    canAddRemoveColumns: true,
    canAddColumnsFreeform: false,
    canAttachFileToCell,
    convertFileToCellData,
    disableFormulas: false,
    disableLegacyHeader: true,
    disableMaximizedTables: false,
    editWhileTableLockedWarning: getLockedTableWarningMessage(LockedTableWarningType.GENERIC),
    forceCollapsed: false,
    getEditWhileCellLockedWarning: genericLockedCellWarningMessage,
    hasPlaceholderRows: false,
    isCollapsible: true,
    isCompactBottomSpacing: false,
    isExpanded: true,
    isLeftAlignColumnHeader: true,
    openManageSelectorsModal: noop,
    selectorsEnabled: false,
    showFormulaBox: props.structuredTableAdapter.shouldShowFormulaBox(props.structuredTable),
    isNestedInParentComponent: false,
    withColumnNameSet: withStructuredTableColumnNameSet,
  };
}

/**
 * Checks that we have the same number of finalized rows when being updated from the TableEditable control.
 * @param structuredTable - the StructuredTable we are attempting to update
 * @param updatedContent - the TableData which contains our updates
 */
function checkFinalizedRowsInvariants<TTableMetadata, TRowMetadata>(
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
  updatedContent: TableData
): void {
  // We don't have a row id here that we can check against, but we can at least check
  // that the positions haven't changed
  const initialFinalizedRowPositions = structuredTable.rows
    .flatMap((row, index) => (row.isFinalized ? index : null))
    .filter((index) => index != null);

  const updatedRowData = updatedContent.rowData;
  assert(updatedContent.rowData != null, 'Updated structured table has no row data');

  const updatedFinalizedRowPositions = updatedRowData!
    .flatMap((rowData, index) => (rowData.isFinalized ? index : null))
    .filter((index) => index != null);

  assert(
    initialFinalizedRowPositions.length <= updatedFinalizedRowPositions.length,
    'Finalized rows may only be added by a table update'
  );
  initialFinalizedRowPositions.forEach((pos) => {
    assert(updatedFinalizedRowPositions.includes(pos), 'Finalized rows may only be added by a table update');
  });
}

function StructuredTableView<TTableMetadata, TRowMetadata>(
  props: StructuredTableViewProps<TTableMetadata, TRowMetadata>
): JSX.Element {
  const {fileId, getIdFromCid, performTableOp, storeState, addAIDataEntryRowsUsingAI} = props.editorProps;
  const [structuredTableMappingsState] = useStructuredTableMappingsState(fileId);
  const {table, getInitialCellValues, staleCellIndices, errorRows, liveRowMetadataByRow, tableStatus} =
    useHydratedStructuredTableData({
      adapter: props.structuredTableAdapter,
      structuredTable: props.structuredTable,
      computedTable: props.itemProps.computedTable,
      validationErrors: props.itemProps.validationErrors,
      options: {
        includeUnsubmittedStatus: BenchlingConfig.ELN_ENABLE_LOOKUP_TABLE_MAPPING
          ? // only keep track of table status for Lookup tables when table mapping is involved
            props.structuredTable.kind !== StructuredTableKind.LOOKUP ||
            StructuredTableMappingsStateManager.hasMappingsFromSourceTable(
              structuredTableMappingsState,
              props.structuredTable.provenanceUuid
            )
          : props.structuredTable.kind !== StructuredTableKind.LOOKUP,
      },
    });

  useOnMount(() => {
    if (props.structuredTable.kind === StructuredTableKind.REGISTRATION) {
      resolveStaleComputingCells(props.structuredTable, fileId);
    }
  });

  // returns the uuids of the rows that do not pass the filters
  const getFilteredRowIds = useCallback(
    (filters: Array<StatusFilter>): Set<string> => {
      if (filters.length === 0) {
        return new Set();
      }

      const errorRowsIndices = errorRows.flatMap((errors, index) => (errors.length > 0 ? index : []));
      const staleRowIndices = staleCellIndices.map((cell) => cell.rowIndex);
      const unsubmittedChangesIndices = table.rowData!.flatMap((row, index) =>
        row.uiData?.hasUnsubmittedChanges ? index : []
      );

      const statusFilterToRelevantArray = {
        [StatusFilter.HAS_ERROR]: errorRowsIndices,
        [StatusFilter.IS_OUT_OF_DATE]: staleRowIndices,
        [StatusFilter.IS_UNSUBMITTED]: unsubmittedChangesIndices,
      };

      const indicesToKeep = new Set(filters.flatMap((filter) => statusFilterToRelevantArray[filter]));
      const filteredRowIds = table
        .rowData!.filter((_, index) => !indicesToKeep.has(index))
        .map((row) => row.rowId!);

      return new Set(filteredRowIds);
    },
    [errorRows, staleCellIndices, table.rowData]
  );

  const isFilterStale = useMemo(() => {
    if (!table.filterInfo) {
      return false;
    }
    const liveFilteredRowIds = new Set(getFilteredRowIds(table.filterInfo?.filters));
    const currentFilteredRowIds = new Set(table.filterInfo.filteredRowIds);

    return !areSetsEqual(liveFilteredRowIds, currentFilteredRowIds);
  }, [getFilteredRowIds, table.filterInfo]);

  const allEditorProps = NoteEditorTableUtil.tableEditablePropsFromEditor(props.editor, props.editorProps);
  const viewer: boolean = allEditorProps.viewer != null ? allEditorProps.viewer : false;
  const structuredTableInsertionContext = StructuredTableInsertionContext.useContext();
  const sharedProps = {
    ...allEditorProps,
    cid: props.notePartCid,
    viewer,
    insertableColumnInfos: getInsertableColumnInfos({
      entryId: props.editorProps.fileId,
      structuredTable: props.structuredTable,
      adapter: props.structuredTableAdapter,
      selectorsById: props.selectorsById,
      structuredTableInsertionContext,
    }),
    ...props.itemProps,
    ...defaultProps(props), // Must be after allEditorProps and itemProps
    ...props.basicTableProps,
    ...props.overrideDefaultTableProps,
  };

  const baseTableOps = useTableOps();
  const {reorderTableRowsOrColumns} = baseTableOps;

  const {structuredTable, updateHandler, structuredTableAdapter, index} = props;

  // Create wrapped deletion functions with data mods support
  const deleteTableRows = useCallback(
    (tableName: string, start: number, end: number): void => {
      performRowDeletionWithDataMods({
        entryId: fileId,
        structuredTable,
        start,
        end,
        onPerformDeletion: () => {
          baseTableOps.deleteTableRows(tableName, start, end);
        },
      });
    },
    [fileId, structuredTable, baseTableOps]
  );

  const deleteTableRowsNonContiguous = useCallback(
    (tableName: string, ranges: Array<[number, number]>): void => {
      performNonContiguousRowDeletionWithDataMods({
        entryId: fileId,
        structuredTable,
        ranges,
        onPerformDeletion: () => {
          baseTableOps.deleteTableRowsNonContiguous(tableName, ranges);
        },
      });
    },
    [fileId, structuredTable, baseTableOps]
  );

  const deleteTableColumns = useCallback(
    (tableName: string, start: number, end: number): void => {
      performColumnDeletionWithDataMods({
        entryId: fileId,
        structuredTable,
        start,
        end,
        onPerformDeletion: () => {
          baseTableOps.deleteTableColumns(tableName, start, end);
        },
      });
    },
    [fileId, structuredTable, baseTableOps]
  );

  const insertTableColumnsLeft = useCallback(
    (tableName: string, start: number, end: number, columnInfo: TableConstants.InsertableColumnInfo | {}) => {
      performColumnCreationWithDataMods({
        entryId: fileId,
        structuredTable,
        start,
        end,
        onPerformCreation: () => {
          baseTableOps.insertTableColumnsLeft(
            tableName,
            start,
            end,
            columnInfo,
            getInitialCellValues(columnInfo)
          );
        },
      });
    },
    [fileId, structuredTable, baseTableOps, getInitialCellValues]
  );

  const insertTableColumnsRight = useCallback(
    (tableName: string, start: number, end: number, columnInfo: TableConstants.InsertableColumnInfo | {}) => {
      performColumnCreationWithDataMods({
        entryId: fileId,
        structuredTable,
        start,
        end,
        onPerformCreation: () => {
          baseTableOps.insertTableColumnsRight(
            tableName,
            start,
            end,
            columnInfo,
            getInitialCellValues(columnInfo)
          );
        },
      });
    },
    [fileId, structuredTable, baseTableOps, getInitialCellValues]
  );

  // Create a new context value with our wrapped deletion functions
  const tableOpsWithDataMods = useMemo(
    () => ({
      ...baseTableOps,
      deleteTableRows,
      deleteTableRowsNonContiguous,
      deleteTableColumns,
      insertTableColumnsLeft,
      insertTableColumnsRight,
    }),
    [
      baseTableOps,
      deleteTableRows,
      deleteTableRowsNonContiguous,
      deleteTableColumns,
      insertTableColumnsLeft,
      insertTableColumnsRight,
    ]
  );

  // This following ref is needed to maintain a pointer to the current state of the structured table across
  // re-renders when the AIDataEntryPopover is triggering edits. Without this, the popover gets a stale
  // table, likely because it is in its own DOM tree that is not immediately getting its props rehydrated
  // when the table is changing.
  const structuredTableRef = useRef(structuredTable);
  useEffect(() => {
    if (structuredTableRef.current !== structuredTable) {
      structuredTableRef.current = structuredTable;
    }
  }, [structuredTable]);

  const performTableOpCb = useCallback(
    ({op, options}: {op: TableConstants.TableOpFunction; options?: TableOpOptions}) => {
      performTableOp({tableName: structuredTable.name, op, options});
    },
    [performTableOp, structuredTable.name]
  );

  const unmergeBrokenSingleInputsBeforeUpdate = useUnmergeBrokenSingleInputsCallback(performTableOpCb);

  /** Updates the structured table at a given document index.
   *
   * If the update breaks Single Inputs, the user will be prompted to convert them to columns before
   * proceeding with the update.
   */
  const indexAwareUpdateHandler = useCallback(
    (
      index: number,
      content: {table: TableData; lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>},
      selectors?: never,
      tableOp?: TableConstants.TableOpDiffMap
    ): void => {
      const {table} = content;
      checkFinalizedRowsInvariants(structuredTable, table);
      const brokenSingleInputIndexes = getBrokenSingleInputIndexes(table);
      if (brokenSingleInputIndexes.length) {
        confirm(
          'Convert single inputs to columns',
          'Updating the table will convert single inputs back into columns.',
          ButtonStyle.NEGATIVE,
          {
            enableRedesign: true,
            onSubmit: () => {
              const unmergedTable = unmergeBrokenSingleInputsBeforeUpdate(table, brokenSingleInputIndexes);
              updateHandler(index, {...content, table: unmergedTable}, selectors, tableOp);
            },
          }
        );
      } else {
        updateHandler(index, content, selectors, tableOp);
      }
    },
    [structuredTable, unmergeBrokenSingleInputsBeforeUpdate, updateHandler]
  );

  /** A partially applied updateHandler that already knows the index */
  const actionFunctionUpdateHandler = useMemo(
    () =>
      updateTableWithColumnUpdatesAndCells(
        index,
        table,
        getIdFromCid,
        indexAwareUpdateHandler,
        getFilteredRowIds
      ),
    [index, table, getIdFromCid, indexAwareUpdateHandler, getFilteredRowIds]
  );

  const {computedTable} = props.itemProps;
  const {note, isConfig} = props.editor.props;

  const tableIsLocked =
    props.editorProps.locked ||
    structuredTableAdapter.isReadOnly() ||
    structuredTableAdapter.areRequiredSchemasArchived(structuredTable);

  const tableIsProtectedAgainstEdit = props.editor.isProtectedAgainstEdit();
  const actionFunctionContext = useActionFunctionContext();
  assert(
    isConfig === undefined || isConfig === structuredTable.isConfig,
    'Editor isConfig must match structured table isConfig if provided'
  );
  const getActionFunctionsParams: GetActionFunctionsParams<TTableMetadata, TRowMetadata> = useMemo(
    () => ({
      entryId: fileId,
      adapter: structuredTableAdapter,
      structuredTable,
      computedTable,
      table, // TODO(BNCH-82951): Need to add the Table type table for now to catch some errors that are only generated in table validation functions.
      liveRowMetadataByRow,
      tableStatus,
      locked: tableIsLocked,
      isProtected: tableIsProtectedAgainstEdit,
      updateHandler: actionFunctionUpdateHandler,
      performTableOp: performTableOpCb,
      note,
      actionFunctionContext,
      isFilterStale,
    }),
    [
      actionFunctionUpdateHandler,
      computedTable,
      fileId,
      tableIsProtectedAgainstEdit,
      liveRowMetadataByRow,
      note,
      structuredTable,
      table,
      structuredTableAdapter,
      tableIsLocked,
      tableStatus,
      performTableOpCb,
      actionFunctionContext,
      isFilterStale,
    ]
  );
  const allowMergeSelection = useAllowMergeSelectionCallback(structuredTable, structuredTableAdapter);
  const allowReorderDimensions = useMemo(
    () => structuredTableAdapter.allowReorderDimensions(structuredTable),
    [structuredTable, structuredTableAdapter]
  );
  const contexts = useStructuredTableContexts();

  const {rightHeaderControls, contextMenuOptionGetters, footerControls} = useMemo(
    () => getActionFunctions(getActionFunctionsParams),
    [getActionFunctionsParams]
  );

  const footerInformation = useMemo(
    () =>
      getFooterInformation({
        entryId: fileId,
        structuredTable,
        adapter: structuredTableAdapter,
      }),
    [fileId, structuredTable, structuredTableAdapter]
  );

  const addAIDataEntryRows = useCallback(
    (
      initialPromptText?: string,
      initialSelectedTableName?: string,
      result?: AddStructuredTableRowsUsingAIResult | null
    ): void => {
      addAIDataEntryRowsUsingAI?.(
        initialPromptText,
        initialSelectedTableName,
        structuredTableRef.current,
        result
      );
    },
    [addAIDataEntryRowsUsingAI]
  );

  const leftHeaderControls = ({isCollapsed}: TableConstants.TableHeaderControlsArgs): JSX.Element | null => {
    if (isCollapsed) {
      return null;
    }

    const insertAfterTable = (noteParts: Array<NotePart>, cidIdMap?: CidIdMap): void => {
      if (cidIdMap) {
        props.editorProps.registerObjects(cidIdMap);
      }
      props.editor.itemInsertHandler(props.index, ...noteParts);
    };

    const addButton = getAddFromSourceButton({
      addOperations: fileId
        ? structuredTableAdapter.getAddFromSourceOperations(fileId, structuredTable, addAIDataEntryRows)
        : [],
      adapter: structuredTableAdapter,
      structuredTable,
      table,
      tableIsLocked,
      updateHandler: (item, tableOp) => {
        props.editorProps.storeState(); // for undo/redo history
        props.updateHandler(
          props.index,
          {item},
          undefined,
          tableOp != null ? {[table.name]: tableOp} : undefined
        );
      },
      contexts,
      insertAfterTable,
    });

    return (
      <div className={css(styles.leftHeaderControls)}>
        {addButton}
        {BenchlingConfig.ELN_ENABLE_AI_DATA_ENTRY &&
          SUPPORTED_TABLE_KINDS_FOR_AI_DATA_ENTRY.includes(structuredTable.kind) &&
          (APIUtil.isEntry(fileId) || APIUtil.isDocument(fileId)) && (
            <AIDataEntryButton
              disabled={tableIsLocked}
              entryId={fileId}
              structuredTable={structuredTable}
              addAIDataEntryRows={addAIDataEntryRows}
              undoState={props.editorProps.undoState}
              dataTestAttrs={{component: 'AIDataEntryButton'}}
            />
          )}
      </div>
    );
  };

  const getSingleInputContextMenuOptions = useGetSingleInputContextMenuOptions({
    tableName: structuredTable.name,
    fileId,
    structuredTable,
    storeState,
  });

  const getExtraContextMenuOptionsForLookup = (
    selectionRange: TableConstants.SelectionRange | null,
    fileAnalyticsParams: FileAnalyticsParams,
    updateColumnConstraint?: (index: number, columnConstraint: TableConstants.ColumnConstraint) => void
  ): Array<ContextMenuOption> => {
    // Legacy workflow entries don't support most kinds of structured tables from the new framework,
    // but they do support box and plate creation tables.
    // To avoid surprises, we can leave Lookup Columns unsupported in those tables for now.
    if (
      !APIUtil.isEntry(fileId) &&
      !APIUtil.isEntryTemplate(fileId) &&
      !APIUtil.isSubtemplate(fileId) &&
      !APIUtil.isDocument(fileId)
    ) {
      return [];
    }

    if (!structuredTableAdapter.enableLookupColumns(structuredTable)) {
      return [];
    }

    assert(
      updateColumnConstraint != null,
      'Column constraint update handler is not getting passed to menu options for lookup columns.'
    );

    // No memoization needed - confirmed as of 2021-04-29 that you can type outside the table
    // without re-rendering the table on every keystroke
    const notebookColumnInfos = structuredTableAdapter.getLookupInputColumns(
      structuredTable,
      table.columnConstraints.filter(
        (columnConstraint): columnConstraint is TableConstants.ColumnConstraint => columnConstraint != null
      )
    );
    return getLookupColumnMenuOptions(
      structuredTable.name,
      table,
      tableIsLocked,
      selectionRange,
      insertTableColumnsLeft,
      insertTableColumnsRight,
      updateColumnConstraint,
      fileAnalyticsParams,
      notebookColumnInfos
    );
  };

  const getExtraContextMenuOptionsForUnifiedAddSample = (
    selectionRange: TableConstants.SelectionRange | null,
    fileAnalyticsParams: FileAnalyticsParams
  ): Array<ContextMenuOption> => {
    if (!DESTINATION_TABLE_TYPES.has(structuredTable.kind)) {
      return [];
    }
    const addSamples = (
      samplesByColumn: Array<{samples: Array<TableConstants.Cell>; columnIndex: number}>,
      options?: AddToTableOptions
    ): void => {
      const addSamplesResult = addSamplesToTable(
        structuredTable,
        structuredTableAdapter,
        table,
        samplesByColumn,
        options
      );
      if (addSamplesResult != null) {
        props.editorProps.storeState(); // for undo/redo history
        props.updateHandler(props.index, {item: addSamplesResult.updatedTable}, undefined, {
          [table.name]: addSamplesResult.tableOp,
        });
        analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.ADDED_SAMPLES_TO_STRUCTURED_TABLE);
      }
    };

    return getUnifiedAddSampleMenuOptions(
      structuredTable,
      structuredTableAdapter,
      tableIsLocked,
      props.editor.props.note,
      props.editor.props.fileId,
      addSamples,
      selectionRange?.minIndex
    );
  };

  const getExtraContextMenuOptionsForCustomColumn = (
    selectionRange: TableConstants.SelectionRange | null,
    fileAnalyticsParams: FileAnalyticsParams,
    updateColumnConstraint?: (columnIndex: number, columnConstraint: TableConstants.ColumnConstraint) => void
  ): Array<ContextMenuOption> => {
    if (!structuredTableAdapter.enableCustomColumns(structuredTable)) {
      return [];
    }

    return getCustomColumnMenuOptions<TTableMetadata, TRowMetadata>(
      structuredTable.name,
      structuredTable,
      table.metadata as StructuredTableToTableMetadata<TTableMetadata>,
      tableIsLocked,
      selectionRange,
      insertTableColumnsLeft,
      insertTableColumnsRight,
      fileAnalyticsParams,
      table,
      updateColumnConstraint
    );
  };

  return (
    <EditorPropertiesContext.Consumer>
      {({rootFolderId, templateCollectionId}) => (
        <TableOpContext.Provider value={tableOpsWithDataMods}>
          <StructuredTableContext.Provider
            // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
            value={{
              structuredTableKind: structuredTable.kind,
              provenanceUuid: structuredTable.provenanceUuid,
            }}
          >
            <TableEditable
              {...sharedProps}
              // We use provenanceUuid instead of the fallback of cid because structured table actions will refresh
              // the entry causing new cids to be generated, which then causes the collapsed state to be reset.
              // By using a stable identifier like provenanceUuid, we ensure that a table that was manually
              // collapsed before an action function remains collapsed after the action completes.
              collapseId={structuredTable.provenanceUuid}
              locked={tableIsLocked}
              isProtected={props.editor.isProtectedAgainstEdit()}
              table={table}
              tableLabel={structuredTableAdapter.getDisplayName(structuredTable)}
              leftHeaderControls={leftHeaderControls}
              headerControls={rightHeaderControls}
              footerControls={footerControls}
              footerInformation={footerInformation}
              staleRows={staleCellIndices.map((cell) => cell.rowIndex)}
              errorRows={errorRows.map((errors) => errors.some((error) => hasAnyErrors(error)))}
              warningRows={errorRows.map((errors) => errors.some((error) => hasAnyWarnings(error)))}
              tableStatus={tableStatus}
              // Structured tables are already rendered with an axis label, whether showExcessLabels is true or false
              showTableRowExcessLabels={false}
              getExtraContextMenuOptions={(selectionRange, updateColumnConstraint) => {
                const fileAnalyticsParams = {
                  fileId,
                  projectId: rootFolderId,
                  templateCollectionId,
                };
                const tableMappingMenuOptions = getStructuredTableMappingContextMenuOptions(
                  selectionRange,
                  fileId,
                  isConfig,
                  note,
                  structuredTable,
                  tableIsLocked,
                  structuredTableMappingsState,
                  (newState) => {
                    // Trigger an update and store state for undo/redo history
                    // Note: storeState has to be called before setting the table mappings so that we can undo to the previous table mappings state.
                    props.editorProps.storeState();
                    // Set the table mappings in Backbone to the new state
                    props.editorProps.setStructuredTableMappings(newState.structuredTableMappings);
                  }
                );
                const unifiedAddSampleMenuOptions = getExtraContextMenuOptionsForUnifiedAddSample(
                  selectionRange,
                  fileAnalyticsParams
                );
                const singleInputMenuOptions = tableIsLocked
                  ? [] // hide single input options when the table is locked
                  : getSingleInputContextMenuOptions(selectionRange, fileAnalyticsParams);
                const lookupMenuOptions = getExtraContextMenuOptionsForLookup(
                  selectionRange,
                  fileAnalyticsParams,
                  updateColumnConstraint
                );
                const customColumnMenuOptions = getExtraContextMenuOptionsForCustomColumn(
                  selectionRange,
                  fileAnalyticsParams,
                  updateColumnConstraint
                );
                const columnSortingMenuOptions = getColumnSortingContextMenuOptions({
                  selectionRange,
                  fileId,
                  table,
                  computedTable,
                  structuredTable,
                  tableIsLocked,
                  onSort: (sortedRowIndices) => {
                    // Trigger an update and store state for undo/redo history
                    // Note: storeState has to be called before reorderRowsOrColumns() so that we can undo to the previous order.
                    props.editorProps.storeState();
                    reorderTableRowsOrColumns(structuredTable.name, {
                      newToOldIndices: sortedRowIndices,
                    });
                  },
                  fileAnalyticsParams,
                  noteIsProtected: note.isProtected,
                });
                return [
                  ...customColumnMenuOptions,
                  ...tableMappingMenuOptions,
                  ...unifiedAddSampleMenuOptions,
                  ...singleInputMenuOptions,
                  ...lookupMenuOptions,
                  ...columnSortingMenuOptions,
                ];
              }}
              getExtraRowAxisContextMenuOptions={contextMenuOptionGetters.rowAxis}
              getInitialCellValues={getInitialCellValues}
              mouseDownHandler={props.mouseDownHandler}
              mouseOverHandler={props.mouseOverHandler}
              updateHandler={(
                index: number,
                {table}: {table: TableConstants.Table},
                selectors?: Optional<Array<Selector>>,
                tableOp?: TableConstants.TableOpDiffMap
              ) => {
                updateHandler(index, {table}, undefined, tableOp);
              }}
              suppressTitleBar={structuredTableAdapter.shouldSuppressTitleBar(structuredTable)}
              allowMergeSelection={allowMergeSelection}
              allowReorderDimensions={allowReorderDimensions}
              dataTestAttrs={{component: 'StructuredTableView', key: structuredTable.kind, element: 'root'}}
              {...forwardSearchResultProps(props)}
            />
          </StructuredTableContext.Provider>
        </TableOpContext.Provider>
      )}
    </EditorPropertiesContext.Consumer>
  );
}

// These styles won't show up properly yet for exports, but we hide the header icons for tables
// automatically when in viewer mode, so we don't need to worry about it.
const styles = StyleSheet.create({
  leftHeaderControls: {display: 'flex', alignItems: 'center', gap: '6px'},
});

export default React.memo(StructuredTableView);
